import type {
  AnswerValue,
  LogicCondition,
  LogicDisplayTarget,
  LogicJump,
  Question,
  QuestionGroupReference,
} from '@/types/form'

function normalizeValue(value: AnswerValue | string | number | string[] | undefined) {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return value
  if (typeof value === 'object') return JSON.stringify(value)
  return value
}

function asNumber(value: unknown) {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const numberValue = Number(value)
    if (!Number.isNaN(numberValue)) return numberValue
    const dateValue = Date.parse(value)
    if (!Number.isNaN(dateValue)) return dateValue
  }
  return Number.NaN
}

function asString(value: unknown) {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function isEqualValue(actual: unknown, expected: unknown) {
  if (typeof actual === 'boolean' || typeof expected === 'boolean') {
    return String(actual) === String(expected)
  }
  return actual === expected
}

function isAnswered(value: unknown) {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

export function evaluateCondition(
  condition: LogicCondition,
  answers: Map<string, AnswerValue>,
  hiddenFields: Record<string, string>,
): boolean {
  const answerValue = answers.has(condition.field)
    ? answers.get(condition.field)
    : hiddenFields[condition.field]
  const actual = normalizeValue(answerValue)
  const expected = condition.value

  switch (condition.operator) {
    case 'equals':
      return Array.isArray(actual)
        ? actual.includes(String(expected))
        : isEqualValue(actual, expected)
    case 'not_equals':
      return Array.isArray(actual)
        ? !actual.includes(String(expected))
        : !isEqualValue(actual, expected)
    case 'contains':
      return asString(actual).toLowerCase().includes(asString(expected).toLowerCase())
    case 'not_contains':
      return !asString(actual).toLowerCase().includes(asString(expected).toLowerCase())
    case 'starts_with':
      return asString(actual).toLowerCase().startsWith(asString(expected).toLowerCase())
    case 'ends_with':
      return asString(actual).toLowerCase().endsWith(asString(expected).toLowerCase())
    case 'greater_than':
      return asNumber(actual) > asNumber(expected)
    case 'less_than':
      return asNumber(actual) < asNumber(expected)
    case 'greater_or_equal':
      return asNumber(actual) >= asNumber(expected)
    case 'less_or_equal':
      return asNumber(actual) <= asNumber(expected)
    case 'is_answered':
      return isAnswered(answerValue)
    case 'is_not_answered':
      return !isAnswered(answerValue)
    case 'includes':
      return Array.isArray(actual) && actual.includes(String(expected))
    case 'not_includes':
      return Array.isArray(actual) && !actual.includes(String(expected))
    case 'between': {
      if (!Array.isArray(expected) || expected.length < 2) return false
      const value = asNumber(actual)
      return value >= asNumber(expected[0]) && value <= asNumber(expected[1])
    }
    default:
      return false
  }
}

export function evaluateLogicJump(
  jump: LogicJump,
  answers: Map<string, AnswerValue>,
  hiddenFields: Record<string, string>,
): boolean {
  const results = jump.conditions.map((condition) =>
    evaluateCondition(condition, answers, hiddenFields),
  )
  return jump.operator === 'AND' ? results.every(Boolean) : results.some(Boolean)
}

function getSequentialNextQuestionId(currentQuestionId: string, allQuestions: Question[]) {
  const currentIndex = allQuestions.findIndex((question) => question.id === currentQuestionId)
  if (currentIndex < 0 || currentIndex >= allQuestions.length - 1) return null
  return allQuestions[currentIndex + 1].id
}

function isQuestion(value: Question | undefined): value is Question {
  return Boolean(value)
}

export function resolveQuestionGroupTargetId(
  targetGroupId: string | null | undefined,
  allQuestions: Question[],
  questionGroups: QuestionGroupReference[] = [],
): string | null {
  if (!targetGroupId) return null

  const questionById = new Map(allQuestions.map((question) => [question.id, question]))
  const group = questionGroups.find((item) => item.id === targetGroupId)
  const explicitQuestionIds = group?.questionIds ?? []
  const groupedQuestionIds =
    explicitQuestionIds.length > 0
      ? explicitQuestionIds
      : allQuestions
          .filter((question) => question.groupId === targetGroupId)
          .sort((left, right) => left.orderIndex - right.orderIndex)
          .map((question) => question.id)

  const firstExistingQuestion = groupedQuestionIds
    .map((questionId) => questionById.get(questionId))
    .filter(isQuestion)[0]

  if (firstExistingQuestion) return firstExistingQuestion.id
  return group?.questions?.[0]?.id ?? null
}

export function resolveDisplayTargetQuestionIds(
  target: LogicDisplayTarget,
  allQuestions: Question[],
  questionGroups: QuestionGroupReference[] = [],
): string[] {
  if (target.type === 'question' || target.type === 'group_question') {
    return allQuestions.some((question) => question.id === target.questionId)
      ? [target.questionId]
      : []
  }

  const questionById = new Map(allQuestions.map((question) => [question.id, question]))
  const group = questionGroups.find((item) => item.id === target.groupId)
  const explicitQuestionIds = group?.questionIds ?? []
  const groupedQuestionIds =
    explicitQuestionIds.length > 0
      ? explicitQuestionIds
      : allQuestions
          .filter((question) => question.groupId === target.groupId)
          .sort((left, right) => left.orderIndex - right.orderIndex)
          .map((question) => question.id)

  return groupedQuestionIds.filter((questionId) => questionById.has(questionId))
}

export function collectDisplayTargetQuestionIds(
  logicJumps: LogicJump[],
  allQuestions: Question[],
  questionGroups: QuestionGroupReference[] = [],
  shouldIncludeJump: (jump: LogicJump) => boolean = () => true,
) {
  const questionIds = new Set<string>()
  for (const jump of logicJumps) {
    if (!shouldIncludeJump(jump)) continue
    for (const target of jump.displayTargets ?? []) {
      for (const questionId of resolveDisplayTargetQuestionIds(
        target,
        allQuestions,
        questionGroups,
      )) {
        questionIds.add(questionId)
      }
    }
  }
  return questionIds
}

export function getNextQuestionId(
  currentQuestionId: string,
  allQuestions: Question[],
  logicJumps: LogicJump[],
  answers: Map<string, AnswerValue>,
  hiddenFields: Record<string, string>,
  questionGroups: QuestionGroupReference[] = [],
): string | null {
  const sequentialNextId = getSequentialNextQuestionId(currentQuestionId, allQuestions)
  const matchingJump = logicJumps
    .filter((jump) => jump.questionId === currentQuestionId)
    .sort((a, b) => a.order - b.order)
    .find((jump) => evaluateLogicJump(jump, answers, hiddenFields))

  if (matchingJump?.targetGroupId) {
    return (
      resolveQuestionGroupTargetId(matchingJump.targetGroupId, allQuestions, questionGroups) ??
      sequentialNextId
    )
  }

  if (matchingJump) return matchingJump.targetQuestionId

  return sequentialNextId
}
